import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Gift, User, Globe, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import backgroundImage from '../assets/trading-background.jpg';
import { supabase } from '../lib/supabaseClient';

const SignUpPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<'signup' | 'email-verification'>('signup');
  const [verificationCode, setVerificationCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [country, setCountry] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const { signUp } = useAuth();

  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam) {
      setReferralCode(refParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-pending-signup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            email,
            password,
            firstName,
            lastName,
            country,
            referralCode,
          }),
        }
      );

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to create account');
      }

      setPendingId(result.pendingId);
      setStep('email-verification');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!pendingId) {
        throw new Error('Pending ID not found');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-email-code`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            userId: pendingId,
            code: verificationCode,
          }),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Invalid verification code');
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (signInError) {
        throw new Error('Failed to sign in after verification');
      }

      sessionStorage.removeItem('marketLoadingShown');
      navigate('/', { replace: true });
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };


  // List of countries for dropdown
  const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde',
  'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China',
  'Colombia', 'Comoros', 'Congo (Brazzaville)', 'Congo (Kinshasa)', 'Costa Rica', 'Croatia',
  'Cuba', 'Cyprus', 'Czechia', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini',
  'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
  'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait',
  'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
  'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco',
  'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia',
  'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay',
  'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa',
  'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
  'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
  'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
  'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste',
  'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
  'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
  'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

  if (step === 'email-verification' && pendingId) {
    return (
      <div className="min-h-screen bg-white">
        {/* Company Name Header */}
        <div className="bg-white w-full border-b border-black/10">
          <div className="max-w-7xl mx-auto px-8 py-4">
            <img src="/logo.svg" alt="Logo" className="h-8" />
          </div>
        </div>

        {/* Full-width Red Header Bar */}
        <div className="bg-red-700 w-full">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <h1 className="text-white text-xl font-medium">
              Verify your email address
            </h1>
          </div>
        </div>

        {/* Verification Content */}
        <div className="bg-white max-w-7xl mx-auto px-8 py-16">
          <div className="max-w-md mx-auto">
            <div className="mb-8">
              <Mail size={48} className="mb-4 text-black" />
              <h2 className="text-2xl font-bold text-black mb-2">Verify Your Email</h2>
              <p className="text-black/60">
                We sent a 6-digit code to <span className="font-semibold text-black">{email}</span>
              </p>
            </div>

            <form onSubmit={handleVerifyEmail} className="space-y-6">
              <div>
                <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="w-full px-4 py-3 bg-white border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors text-center text-2xl tracking-widest font-mono text-black placeholder:text-black/30"
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-red-700 border-l-2 border-red-700 pl-4 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="w-full bg-transparent text-red-700 hover:text-red-900 disabled:text-black/20 py-4 font-semibold transition-colors flex items-center justify-center gap-2 border border-red-700 hover:border-red-900 disabled:border-black/10 rounded"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/10 border-t-red-700 animate-spin" />
                ) : (
                  <>
                    Verify Email
                    <ChevronRight size={16} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-black/60">
              Didn't receive the code? Check your spam folder or{' '}
              <button
                onClick={handleSubmit}
                className="text-black hover:text-red-700 transition-colors font-medium underline"
              >
                resend code
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-white">
      {/* Company Name Header */}
      <div className="bg-white w-full border-b border-black/10">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <img src="/logo.svg" alt="Logo" className="h-8" />
        </div>
      </div>

      {/* Full-width Red Header Bar */}
      <div className="bg-red-700 w-full">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <h1 className="text-white text-xl font-medium">
            Create your trading account
          </h1>
          <div className="[&_button]:bg-transparent [&_button]:border-white/20 [&_button]:hover:border-white/40 [&_button]:text-white [&_svg]:text-white/60 [&_.absolute]:bg-white [&_.absolute]:border-black/15 [&_.absolute]:rounded-none [&_.absolute_button]:hover:bg-black/5 [&_.absolute_button]:text-black [&_.absolute_button.bg-slate-700\\/50]:bg-transparent [&_.absolute_button.bg-slate-700\\/50]:text-red-700 [&_.absolute_button.bg-slate-700\\/50]:border-l-2 [&_.absolute_button.bg-slate-700\\/50]:border-l-red-700 [&_.absolute_svg.text-blue-400]:text-red-700">
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {/* Three-Column Layout */}
      <div className="bg-white max-w-7xl mx-auto px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* LEFT COLUMN - Sign Up Form */}
          <div className="max-w-md">
            {error && (
              <div className="mb-6 text-sm text-red-700 border-l-2 border-red-700 pl-4 py-2">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">
                    {t('auth.firstName')} <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('auth.firstName')}
                    className="w-full bg-white text-black px-4 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">
                    {t('auth.lastName')} <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t('auth.lastName')}
                    className="w-full bg-white text-black px-4 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">
                  {t('auth.country')} <span className="text-red-700">*</span>
                </label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full bg-white text-black px-4 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors"
                  required
                >
                  <option value="">{t('auth.selectYourCountry')}</option>
                  {countries.map(country => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">
                  {t('auth.email')} <span className="text-red-700">*</span>
                </label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black/30" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.enterYourEmail')}
                    className="w-full bg-white text-black pl-10 pr-4 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">
                  {t('auth.password')} <span className="text-red-700">*</span>
                </label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black/30" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.createAPassword')}
                    className="w-full bg-white text-black pl-10 pr-10 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black/30 hover:text-black transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-black/50 mt-1">
                  {t('auth.mustBeAtLeast6Characters')}
                </p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">
                  {t('auth.confirmPassword')} <span className="text-red-700">*</span>
                </label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black/30" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.confirmYourPassword')}
                    className="w-full bg-white text-black pl-10 pr-10 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black/30 hover:text-black transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-black/70 mb-2 font-medium">
                  {t('auth.referralCode')} <span className="text-black/40">({t('common.optional')})</span>
                </label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  placeholder={t('auth.enterReferralCode')}
                  className="w-full bg-white text-black px-4 py-3 border border-black/15 rounded focus:outline-none focus:border-red-700 transition-colors placeholder:text-black/30"
                />
                <p className="text-xs text-black/50 mt-1">
                  {t('auth.getTradingBonuses')}
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#d91222] text-white hover:text-red-900 disabled:text-black/20 py-4 font-semibold transition-colors flex items-center justify-center gap-2 mt-2 border border-red-700 hover:border-red-900 disabled:border-black/10 rounded"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/10 border-t-red-700 animate-spin" />
                ) : (
                  <>
                    {t('auth.createAccount')}
                    <ChevronRight size={16} />
                  </>
                )}
              </button>

              <div className="mt-8 pt-6 border-t border-black/10">
                <p className="text-black/70 text-sm">
                  {t('auth.alreadyHaveAccount')}{' '}
                  <Link to="/signin" className="text-black hover:text-red-700 transition-colors font-medium underline">
                    {t('auth.signIn')}
                  </Link>
                </p>
              </div>

              <div className="mt-4 text-xs text-black/50">
                {t('auth.byCreatingAccount')}{' '}
                <a href="#" className="text-black/70 hover:text-red-700 transition-colors underline">
                  {t('auth.termsOfService')}
                </a>{' '}
                {t('auth.and')}{' '}
                <a href="#" className="text-black/70 hover:text-red-700 transition-colors underline">
                  {t('auth.privacyPolicy')}
                </a>
              </div>
            </form>
          </div>

          {/* CENTER COLUMN - Bitcoin Visual */}
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <img
              src="https://sunny-gaufre-664069.netlify.app/capital/en/1.png"
              alt="Bitcoin Guide"
              className="w-full rounded-lg"
            />
            <p className="text-lg text-black/70 font-medium">
              Secure, convenient trading anytime
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;